// Libraries
import PropTypes from 'prop-types';
import { useContext, useId, useState } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';

// Components
import { InfoCircleFilled } from '@ant-design/icons';
import { Popover } from 'antd';

// Dependencies
import TranslationsContext from 'providers/translationsProvider';
import bfbTheme from 'styles/b4b-theme';

const TOOL6_SLUG = 'financial-projections';

const InfoBubble = ({
  infotext,
  placement = 'bottomRight',
  style = null,
  HTMLinfotext = null,
  ...rest
}) => {
  const { t } = useContext(TranslationsContext);
  const params = useParams();
  const { slug } = params;
  const tooltipId = useId();
  const [isOpen, setIsOpen] = useState(false);
  /*
  Switch function which decides which formatting to use for bubbles based upon current step
  */
  let infoContent;

  switch (slug) {
    case TOOL6_SLUG:
      infoContent = <div style={{ maxWidth: 700 }}>{infotext}</div>;
      break;
    default:
      infoContent = HTMLinfotext ? (
        <StyledDiv dangerouslySetInnerHTML={{ __html: HTMLinfotext }} />
      ) : (
        <StyledDiv>{infotext}</StyledDiv>
      );
  }

  return (
    <StyledContainer style={style}>
      <Popover
        placement={placement}
        {...rest}
        trigger={['hover', 'click']}
        content={<div id={tooltipId}>{infoContent}</div>}
        getPopupContainer={trigger => trigger.parentElement}
        onOpenChange={setIsOpen}
      >
        <StyledButton type="button" aria-label={t('More information')} aria-describedby={isOpen ? tooltipId : undefined}>
          <StyledInfoIcon aria-hidden="true" />
        </StyledButton>
      </Popover>
    </StyledContainer>
  );
};

InfoBubble.propTypes = {
  infotext: PropTypes.node.isRequired,
  HTMLinfotext: PropTypes.string,
  placement: PropTypes.string,
  style: PropTypes.instanceOf(Object),
};

const StyledContainer = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  min-width: 24px;
  min-height: 24px;
`;

const StyledInfoIcon = styled(InfoCircleFilled)`
  &&& {
    font-size: 20px;
    color: ${bfbTheme.placeholderGrey};
    transition: all ease-in 0.15s;

    ${StyledButton}:hover &,
    ${StyledButton}:focus & {
      color: ${bfbTheme.teal};
    }
  }
`;

const StyledDiv = styled.div`
  color: black;
  margin-bottom: 0;
  min-width: 250px;
  max-width: 400px;
  padding: 8px 4px;
`;

export default InfoBubble;
